import {
  Component,
  ElementRef,
  effect,
  inject,
  input,
  InputSignal,
  PLATFORM_ID,
  Signal,
  signal,
  untracked,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {
  Coordinate,
  FormatDurationPipe,
  MainColorsRef,
  TrainingSection,
} from '@kinomap-dev/ng-ui-lib/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { PulseComponent, WheelLoader } from '@kinomap-dev/ng-ui-lib/loaders';
import { MediaBlockService } from '../../services/media-block-service';
import { isPlatformBrowser, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { take } from 'rxjs';
import { ProfileService } from '../../services/profile.service';
import { FormGroup } from '@angular/forms';
import { CreateContentService } from '../../services/create-content.service';
import { GpsService } from '../../services/gps.service';
import { ContentType } from '../../pages/content-creation/content-creation';
import { ColorService } from '../../services/color.service';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { LeafletService } from '../../services/leaflet';
import { Map } from '../map/map';
import { AutoSyncService } from '../../services/auto-sync';
import { GpsUploadControlItem } from './gps-upload-control-item';

import { TranslateModule } from '@ngx-translate/core';
@Component({
  selector: 'app-gps-uploader',
  imports: [TranslateModule, 
    PulseComponent,
    TitleCasePipe,
    IconComponent,
    Map,
    FileSizePipe,
    UpperCasePipe,
    FormatDurationPipe,
    WheelLoader,
    GpsUploadControlItem,
  ],
  templateUrl: './gps-uploader.html',
  styleUrl: './gps-uploader.scss',
  providers: [LeafletService],
})
export class GpsUploader {
  mediaBlockService: MediaBlockService = inject(MediaBlockService);
  private readonly profileService: ProfileService = inject(ProfileService);
  readonly createContentService: CreateContentService = inject(CreateContentService);
  private readonly gpsService: GpsService = inject(GpsService);
  readonly colorService: ColorService = inject(ColorService);
  readonly mediaForm: InputSignal<FormGroup> = input.required<FormGroup>();
  readonly contentType: InputSignal<ContentType> = input.required<ContentType>();

  inputRef: Signal<ElementRef<HTMLInputElement>> = viewChild.required('gpsInput', {
    read: ElementRef<HTMLInputElement>,
  });

  isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));

  public readonly autoSyncService: AutoSyncService = inject(AutoSyncService);

  loaderColor: MainColorsRef = MainColorsRef.dark;
  gpsFilesSupport: string[] = this.mediaBlockService.gpsFilesSupport;
  gpsAccept: string = this.gpsFilesSupport.map((extention: string) => '.' + extention).join(',');
  isGpxDragOver = false;
  trainingSections: WritableSignal<TrainingSection[]> = signal<TrainingSection[]>([]);

  // Les contrôles de validité (état pending/in_progress/complete/error) vivent dans
  // MediaBlockService : app-gps-uploader peut être instancié à la fois dans le bloc
  // média et dans la fenêtre fullscreen, et les deux instances doivent refléter le
  // même résultat de contrôle pour un même tracé.
  isLoadingTrack: WritableSignal<boolean> = this.mediaBlockService.isLoadingTrack;
  controlGpsCompatibility = this.mediaBlockService.controlGpsCompatibility;
  controlGpsDuration = this.mediaBlockService.controlGpsDuration;
  controlGpsSpeed = this.mediaBlockService.controlGpsSpeed;
  controlGpsData = this.mediaBlockService.controlGpsData;
  controlsDone: Signal<boolean> = this.mediaBlockService.controlsDone;
  controlsStarted: Signal<boolean> = this.mediaBlockService.controlsStarted;
  controlsHaveError: Signal<boolean> = this.mediaBlockService.controlsHaveError;

  get rawCoordinates() {
    return this.mediaBlockService.syncedCoordinates;
  }

  constructor() {
    effect(() => {
      this.profileService.profile();
      if (!this.mediaBlockService.syncedCoordinates().length) return;
      untracked(() => this.mediaBlockService.updateMapInformations());
    });

    if (
      this.mediaBlockService.gpsFile() &&
      this.mediaBlockService.syncedCoordinates().length === 0
    ) {
      this.isLoadingTrack.set(true);
      this.gpsService.formatXmlToCoordinates(this.mediaBlockService.gpsFile()!).then((obs) => {
        obs.pipe(take(1)).subscribe({
          next: (coordinates: Coordinate[]) => {
            this.mediaBlockService.syncedCoordinates.set(coordinates);
            this.createContentService.coordinates.set(coordinates);
            const ext: string = this.createContentService.gpsExtention() || '';
            this.mediaBlockService.runGpsControls(ext, coordinates);
          },
          complete: () => this.isLoadingTrack.set(false),
        });
      });
    }
  }

  setIsGpxDragOver(event: DragEvent, value: boolean): void {
    event.preventDefault();
    this.isGpxDragOver = value;
  }

  async onSelectGpxFile(event: Event): Promise<void> {
    const file: File | undefined = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const ext: string = file.name.split('.').pop()?.toLowerCase() || '';
      if (this.gpsFilesSupport.includes(ext)) {
        this.mediaBlockService.gpsFile.set(file);
        this.createContentService.gpsExtention.set(ext);
        this.isLoadingTrack.set(true);
        (await this.gpsService.formatXmlToCoordinates(file)).pipe(take(1)).subscribe({
          next: (coordinates: Coordinate[]) => {
            this.rawCoordinates.set(coordinates);
            this.createContentService.coordinates.set(coordinates);
            this.mediaForm().get('coordinates')?.setValue(coordinates);
            if (coordinates.length > 0) {
              const firstCooTime: number = coordinates[0]!.coo_time;
              const lastCooTime: number = coordinates.at(-1)!.coo_time;
              const hasHeartRate: boolean = coordinates.some(
                (c: Coordinate) => c.hearth_rate !== undefined,
              );
              const hasCadence: boolean = coordinates.some(
                (c: Coordinate) => c.cadence !== undefined,
              );
              const hasPower: boolean = coordinates.some((c: Coordinate) => c.power !== undefined);
              this.mediaBlockService.gpsFileData.set({
                name: file.name,
                weight: file.size,
                duration: lastCooTime - firstCooTime,
                cadence: hasCadence,
                heartRate: hasHeartRate,
                power: hasPower,
              });
              this.mediaBlockService.chartData.set(
                coordinates.map((coordinate: Coordinate) => coordinate.elevation),
              );
              this.mediaBlockService.chartLabels.set(
                coordinates.map((coordinate: Coordinate) => coordinate.coo_time),
              );
              this.mediaBlockService.chartDistances.set(
                coordinates.map((coordinate: Coordinate) => coordinate.distance),
              );
              if (this.contentType() === 'gps-only') {
                const recordDate: Date = this.gpsService.getRecordedDate(coordinates);
                this.createContentService.recordDate.set(recordDate);
              }
            }
            this.mediaBlockService.runGpsControls(ext, coordinates);
          },
          complete: () => {
            this.isLoadingTrack.set(false);
            if (this.contentType() === 'gps-only') {
              this.createContentService.uploadDate.set(new Date());
            }
          },
        });
      }
    }
  }

  async onGpxDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isGpxDragOver = false;
    const files: FileList | null = event.dataTransfer?.files || null;
    if (files && files.length > 0) {
      const file: File = files[0];
      const ext: string = file.name.split('.').pop()?.toLowerCase() || '';
      if (this.gpsFilesSupport.includes(ext)) {
        this.mediaBlockService.gpsFile.set(file);
        this.createContentService.gpsExtention.set(ext);
        this.isLoadingTrack.set(true);
        (await this.gpsService.formatXmlToCoordinates(file)).pipe(take(1)).subscribe({
          next: (coordinates: Coordinate[]) => {
            this.rawCoordinates.set(coordinates);
            this.createContentService.coordinates.set(coordinates);
            this.mediaForm().get('coordinates')?.setValue(coordinates);
            if (coordinates.length > 0) {
              const firstCooTime: number = coordinates[0]!.coo_time;
              const lastCooTime: number = coordinates.at(-1)!.coo_time;
              const hasHeartRate: boolean = coordinates.some(
                (c: Coordinate) => c.hearth_rate !== undefined,
              );
              const hasCadence: boolean = coordinates.some(
                (c: Coordinate) => c.cadence !== undefined,
              );
              const hasPower: boolean = coordinates.some((c: Coordinate) => c.power !== undefined);
              this.mediaBlockService.gpsFileData.set({
                name: file.name,
                weight: file.size,
                duration: lastCooTime - firstCooTime,
                cadence: hasCadence,
                heartRate: hasHeartRate,
                power: hasPower,
              });
              this.mediaBlockService.chartData.set(
                coordinates.map((coordinate: Coordinate) => coordinate.elevation),
              );
              this.mediaBlockService.chartLabels.set(
                coordinates.map((coordinate: Coordinate) => coordinate.coo_time),
              );
              this.mediaBlockService.chartDistances.set(
                coordinates.map((coordinate: Coordinate) => coordinate.distance),
              );
              if (this.contentType() === 'gps-only') {
                const recordDate: Date = this.gpsService.getRecordedDate(coordinates);
                this.createContentService.recordDate.set(recordDate);
              }
            }
            this.mediaBlockService.runGpsControls(ext, coordinates);
          },
          complete: () => {
            this.isLoadingTrack.set(false);
            if (this.contentType() === 'gps-only') {
              this.createContentService.uploadDate.set(new Date());
            }
          },
        });
      } else {
        alert(
          `Merci de déposer un fichier GPS (${this.gpsFilesSupport
            .map((extention: string) => '.' + extention)
            .join(', ')}).`,
        );
      }
    }
  }

  onRemoveGPS(): void {
    this.mediaBlockService.gpsFile.set(undefined);
    this.rawCoordinates.set([]);
    this.mediaBlockService.syncedCoordinates.set([]);
    this.mediaBlockService.gpsTrackValidated.set(false);
    this.mediaBlockService.resetControls();
    this.createContentService.coordinates.set([]);
    this.mediaForm().get('coordinates')?.setValue(null);
    this.mediaBlockService.chartData.set(undefined);
    this.mediaBlockService.chartLabels.set(undefined);
    this.mediaBlockService.chartDistances.set(undefined);
    this.mediaBlockService.gpsFileData.set(undefined);
    this.mediaBlockService.clearSync();
    if (this.inputRef()) this.inputRef().nativeElement.value = '';
  }
}
